import { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Bell, Loader2 } from 'lucide-react';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Badge } from '@/components/ui/badge';

/**
 * Notification bell shared by both admin and staff worlds.
 *
 * Props:
 *  - fetcher: () => Promise<{count, items: Task[]}>
 *  - markRead: (taskId) => Promise<void>
 *  - onOpenTask: (task) => void   // open task drawer or navigate
 *  - className: optional
 *  - testId
 */
export function TaskNotificationBell({ fetcher, markRead, onOpenTask, className = '', testId = 'task-notif-bell' }) {
  const [data, setData] = useState({ count: 0, items: [] });
  const [loading, setLoading] = useState(false);
  const [open, setOpen] = useState(false);

  const refresh = useCallback(async () => {
    if (!fetcher) return;
    setLoading(true);
    try { setData(await fetcher()); } catch { /* silent — bell shouldn't break the page */ }
    finally { setLoading(false); }
  }, [fetcher]);

  useEffect(() => { refresh(); }, [refresh]);
  // Poll every 60s while mounted
  useEffect(() => {
    const id = setInterval(refresh, 60000);
    return () => clearInterval(id);
  }, [refresh]);

  const handleItemClick = async (task) => {
    try { await markRead?.(task.id); } catch {}
    setOpen(false);
    onOpenTask?.(task);
    refresh();
  };

  return (
    <Popover open={open} onOpenChange={(v) => { setOpen(v); if (v) refresh(); }}>
      <PopoverTrigger asChild>
        <button
          type="button"
          className={`relative inline-flex items-center justify-center h-9 w-9 rounded-full hover:bg-muted/60 transition-colors ${className}`}
          aria-label="Notifications"
          data-testid={testId}
        >
          <Bell className="h-4 w-4 text-[#1f2933]" />
          {data.count > 0 && (
            <span className="absolute -top-0.5 -right-0.5 min-w-[18px] h-[18px] px-1 flex items-center justify-center rounded-full bg-emerald-600 text-white text-[10px] font-semibold" data-testid={`${testId}-count`}>
              {data.count}
            </span>
          )}
        </button>
      </PopoverTrigger>
      <PopoverContent align="end" className="w-80 p-0" data-testid={`${testId}-popover`}>
        <div className="px-3 py-2 border-b flex items-center justify-between">
          <p className="text-sm font-semibold">Notifications</p>
          {loading && <Loader2 className="h-3 w-3 animate-spin text-muted-foreground" />}
        </div>
        <div className="max-h-[420px] overflow-y-auto divide-y">
          {data.items.length === 0 ? (
            <p className="text-xs text-muted-foreground p-4 text-center">You're all caught up.</p>
          ) : data.items.map(t => (
            <button
              key={t.id}
              onClick={() => handleItemClick(t)}
              className="w-full text-left px-3 py-2.5 hover:bg-muted/40 transition-colors"
              data-testid={`notif-item-${t.id}`}
            >
              <p className="text-sm font-medium text-[#1f2933] truncate">{t.title}</p>
              <p className="text-[11px] text-muted-foreground truncate">
                {t.assignee_name && <>{t.assignee_name} · </>}
                {t.project_name ? `${t.project_name}` : 'Standalone'}
                {t.status === 'done' && ' · Marked done'}
              </p>
              {t.thread && t.thread.length > 0 && (
                <p className="text-[11px] text-muted-foreground truncate mt-0.5">
                  💬 {t.thread[t.thread.length - 1].body}
                </p>
              )}
            </button>
          ))}
        </div>
      </PopoverContent>
    </Popover>
  );
}
